<template>
  <v-app class="bg-gray-50">
    <v-container fluid class="pa-4 pa-md-8">
      <div class="d-flex flex-column flex-md-row justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold grey--text text--darken-3 mb-1">
            Notas Fiscais
          </h1>
          <p class="subtitle-1 grey--text text--darken-1 mb-0">
            Gerencie suas notas fiscais de produtos
          </p>
        </div>
        
        <v-btn
          color="primary"
          class="text-capitalize px-6 mt-4 mt-md-0 elevation-2 btn-gradient"
          rounded
          height="44"
          @click="newNFE()"
        >
          <v-icon left size="20">{{ icons.mdiPlus }}</v-icon>
          Nova NF-E
        </v-btn>
      </div>

      <v-card class="rounded-xl elevation-1 overflow-hidden" outlined>
        <div class="px-6 py-5 d-flex flex-column flex-md-row align-center justify-space-between bg-white border-bottom">
          <div class="d-flex align-center flex-grow-1 w-full max-width-600">
            <v-text-field
              v-model="searchQuery"
              placeholder="Pesquisar por número, chave ou status..."
              outlined
              hide-details
              dense
              rounded
              background-color="grey lighten-5"
              class="search-field"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  class="ml-3 filter-btn"
                  :color="filterApply ? 'primary' : 'grey darken-1'"
                  v-bind="attrs"
                  v-on="on"
                  @click="applyFilter()"
                >
                  <v-icon>{{ icons.mdiFilter }}</v-icon>
                </v-btn>
              </template>
              <span>{{ filterApply ? "Remover Filtros" : "Filtrar" }}</span>
            </v-tooltip>
          </div>

          <transition name="fade">
            <div 
              class="d-flex align-center mt-3 mt-md-0 ml-md-4" 
              v-if="selectedInvoices.length > 0"
            >
              <span class="caption grey--text mr-3 hidden-sm-and-down">
                {{ selectedInvoices.length }} selecionados
              </span>
              <v-btn
                small
                text
                color="secondary"
                class="text-capitalize mr-2"
                @click="reSend(getIdsOfSelectedsInvoices())"
              >
                <v-icon left small>{{ icons.mdiRestore }}</v-icon>
                Reenviar
              </v-btn>
              <v-btn
                small
                text
                color="primary"
                class="text-capitalize"
                @click="consult(getIdsOfSelectedsInvoices())"
              >
                <v-icon left small>{{ icons.mdiConsole }}</v-icon>
                Consultar
              </v-btn>
            </div>
          </transition>
        </div>

        <v-data-table
          :items-per-page="25"
          :page.sync="page"
          show-select
          :search="searchQuery"
          item-key="id"
          v-model="selectedInvoices"
          :headers="tableColumns"
          :items="invoices"
          :loading="loading"
          loading-text="Carregando notas fiscais..."
          @page-count="pageCount = $event"
          class="custom-table"
          :footer-props="{
            itemsPerPageOptions: [25, 50, 100],
            itemsPerPageText: 'Itens por página',
            itemsPerPageAllText: 'Todos'
          }"
        >
          <template v-slot:header.data-table-select="{ props, on }">
            <v-simple-checkbox
              v-bind="props"
              v-on="on"
              color="primary"
              :ripple="false"
            ></v-simple-checkbox>
          </template>

          <template v-slot:item.data-table-select="{ item, isSelected, select }">
            <v-simple-checkbox
              :value="isSelected"
              color="primary"
              :ripple="false"
              :disabled="item.status === 'billed'"
              @input="select($event)"
              class="ma-0"
            ></v-simple-checkbox>
          </template>

          <template v-slot:item.number="{ item }">
             <span class="font-weight-medium grey--text text--darken-3">
               {{ item.number || '-' }}
             </span>
          </template>

          <template v-slot:item.customer="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="font-weight-medium text-body-2">{{ item.customer ? item.customer.name : 'Consumidor Final' }}</span>
              <span class="caption grey--text text--lighten-1" v-if="item.customer && item.customer.document">
                {{ item.customer.document | cpfCnpj }}
              </span>
            </div>
          </template>

          <template v-slot:item.chave="{ item }">
            <v-tooltip bottom content-class="custom-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span 
                  v-bind="attrs" 
                  v-on="on" 
                  class="text-truncate d-inline-block caption mono-font" 
                  style="max-width: 150px; cursor: help"
                >
                  {{ item.chave || '---' }}
                </span>
              </template>
              <span class="mono-font">{{ item.chave }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.value="{ item }">
            <span class="font-weight-bold success--text text--darken-1">
              {{ (item.value / 100) | moneyBr }}
            </span>
          </template>

          <template v-slot:item.emited_at="{ item }">
            <span class="grey--text text--darken-2 body-2">
              {{ item.emited_at | dateBR }}
            </span>
          </template>

          <template v-slot:item.link="{ item }">
            <v-btn
              v-if="item.link"
              icon
              small
              color="primary"
              :href="item.link"
              target="_blank"
              title="Abrir PDF Externo"
            >
              <v-icon small>{{ icons.mdiFilePdfBox }}</v-icon>
            </v-btn>
            <span v-else class="grey--text text--lighten-2">-</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="resolveStatusVariant(item.status)"
              text-color="white"
              class="font-weight-semibold letter-spacing-1 text-uppercase px-3"
              style="height: 24px; font-size: 10px;"
            >
              {{ resolveStatusDescription(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
             <v-menu bottom left offset-y content-class="rounded-lg shadow-lg">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" color="grey darken-1">
                  <v-icon small>{{ icons.mdiDotsVertical }}</v-icon>
                </v-btn>
              </template>

              <v-list dense class="py-1">
                <v-list-item @click="consult(item.id)">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="primary">{{ icons.mdiConsole }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">Consultar Sefaz</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item :to="{ name: 'products-invoices-details', params: { id: item.id } }">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="info">{{ icons.mdiInformationOutline }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">Detalhes</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-1"></v-divider>

                <v-list-item
                  @click="downloadPdf(item.chave)"
                  :disabled="!item.chave || item.status !== 100"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="error">{{ icons.mdiFilePdfBox }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">Baixar PDF</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item
                  @click="downloadXml(item.chave)"
                  :disabled="!item.chave || item.status !== 100"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="indigo">{{ icons.mdiFileXmlBox }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">Baixar XML</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-1"></v-divider>
                
                <v-list-item @click="openDialogCancel(item.chave)">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">{{ icons.mdiDeleteOutline }}</v-icon>
                  </v-list-item-icon>
                   <v-list-item-content>
                    <v-list-item-title class="body-2">Cancelar Nota</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:item.messages="{ item }">
             <v-tooltip bottom content-class="custom-tooltip" max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <div 
                  v-bind="attrs" 
                  v-on="on" 
                  class="text-truncate grey--text text--darken-2" 
                  style="max-width: 200px; cursor: help"
                >
                  {{ item.messages || '---' }}
                </div>
              </template>
              <span>{{ item.messages || 'Sem mensagem' }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="d-flex justify-center align-center flex-column my-12 py-12">
              <v-img
                contain
                max-width="200"
                src="@/assets/images/svg/empty.svg" 
                class="mb-6 opacity-70"
              >
                  <template v-slot:placeholder>
                     <v-icon size="80" color="grey lighten-3">{{ icons.mdiFileDocumentOutline }}</v-icon>
                  </template>
              </v-img>
              <h3 class="text-h6 grey--text text--darken-2 font-weight-medium">Nenhuma nota fiscal encontrada</h3>
              <p class="body-2 grey--text mt-2 mb-6" style="max-width: 400px; text-align: center;">
                Comece a gerenciar suas vendas emitindo sua primeira nota fiscal eletrônica.
              </p>
              <v-btn
                color="primary"
                outlined
                rounded
                class="text-capitalize px-6"
                @click="newNFE()"
              >
                <v-icon left size="18">{{ icons.mdiPlus }}</v-icon>
                Emitir Nova Nota
              </v-btn>
            </div>
          </template>

          <template v-slot:no-results>
            <div class="d-flex flex-column align-center justify-center py-10">
               <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-magnify-off</v-icon>
               <span class="grey--text text--darken-1">Nenhum resultado encontrado para "{{ searchQuery }}"</span>
               <v-btn text color="primary" small class="mt-2 text-capitalize" @click="searchQuery = ''">Limpar pesquisa</v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div v-if="loading" class="d-flex justify-center pa-12">
        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
      </div>

      <outgoingInvoicesFilterComponent
        ref="filterForm"
        @filter="filter($event)"
      ></outgoingInvoicesFilterComponent>
      
      <outgoingInvoiceForm
        ref="form"
        @onSubmit="setInvoice($event)"
      ></outgoingInvoiceForm>
      
      <json-dialog ref="jsonDialog" />
      <cancel-product-form ref="cancelForm" />
    </v-container>
  </v-app>
</template>

<script>
import {
  mdiDeleteOutline,
  mdiDotsVertical,
  mdiFileDocumentOutline,
  mdiFilter,
  mdiPlus,
  mdiRestore,
  mdiConsole,
  mdiDownload,
  mdiFilePdfBox,
  mdiFileXmlBox,
  mdiInformationOutline,
} from "@mdi/js";

import outgoingInvoicesFilterComponent from "@/components/app/filters/outgoingInvoices/outgoingInvoicesFilterComponent";
import outgoingInvoiceForm from "@/components/outgoingInvoice/form/outgoingInvoiceForm";

import NothingComponent from "@/components/nothingComponent.vue";
import JsonDialog from "@/components/app/JsonDialog.vue";
import CancelProductForm from "@/components/outgoingInvoice/form/cancelProductForm.vue";
import statusNotaMixin from "@/util/mixins/statusNotaMixin";

export default {
  name: "ProductsInvoicesScreen",
  mixins: [statusNotaMixin],
  components: {
    outgoingInvoicesFilterComponent,
    outgoingInvoiceForm,
    NothingComponent,
    JsonDialog,
    CancelProductForm,
  },

  data() {
    return {
      icons: {
        mdiFilter,
        mdiDeleteOutline,
        mdiDotsVertical,
        mdiFileDocumentOutline,
        mdiPlus,
        mdiRestore,
        mdiConsole,
        mdiDownload,
        mdiFilePdfBox,
        mdiFileXmlBox,
        mdiInformationOutline,
      },

      searchQuery: null,
      filterApply: false,
      loading: false,

      invoices: [],
      filteredInvoices: [],
      selectedInvoices: [],
      selected: false,
      page: null,
      pageCount: null,

      tableColumns: [
        { text: "Numero", value: "number" },
        { text: "Chave", value: "chave" },
        { text: "Mensagem", value: "messages" },
        { text: "Cliente", value: "customer" },
        { text: "Valor", value: "value" },
        { text: "Data de Emissão", value: "emited_at" },
        { text: "Link", value: "link" },
        { text: "Status", value: "status" },
        { text: "Ações", value: "actions" },
      ],

      itemsFiltered: null,
    };
  },

  computed: {
    invoiceShow() {
      return this.filterApply ? this.filteredInvoices : this.invoices;
    },
  },

  mounted() {
    this.getInvoices();
  },

  methods: {
    openJsonDialog(item) {
      this.$refs.jsonDialog.open(item);
    },

    getInvoices() {
      this.loading = true;
      this.$http
        .index("/nota-fiscal-produto", this.itemsFiltered)
        .then((response) => {
          this.invoices = response.data.data;
        })
        .catch((error) => {
          console.log(error);
        });
      this.loading = false;
    },

    newNFE() {
      this.$refs.form.open();
    },

    getIdsOfSelectedsInvoices() {
      var id = [];

      this.selectedInvoices.forEach((item) => {
        id.push(item.id);
      });

      return id;
    },

    reSend(nota_id) {
      this.$http
        .$post("/nota-fiscal-resend", { id: nota_id })
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });
    },

    consult(id) {
      this.$http
        .$get("/nota-fiscal-produto-consult/", { chave: id })
        .then((res) => {
          this.$toast.success("NFe consultada com sucesso!");
          this.getInvoices();
        })
        .catch((err) => {
          this.$toast.error("Erro ao consultar NFe");
          console.log(err);
        });
    },

    async downloadPdf(chave) {
      if (!chave) {
        this.$toast.error("NFe não possui chave de acesso");
        return;
      }
      try {
        const response = await this.$http.$get(`/nfe/danfe-base64/${chave}`);
        if (response.pdf) {
          const link = document.createElement("a");
          link.href = `data:application/pdf;base64,${response.pdf}`;
          link.download = response.filename || `DANFE_${chave}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.$toast.success("PDF baixado com sucesso!");
        } else {
          this.$toast.error("Erro ao gerar PDF");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Erro ao baixar PDF: " + (err.message || "Erro desconhecido"));
      }
    },

    async downloadXml(chave) {
      if (!chave) {
        this.$toast.error("NFe não possui chave de acesso");
        return;
      }
      try {
        const response = await this.$http.$get(`/nfe/xml-base64/${chave}`);
        if (response.xml) {
          const link = document.createElement("a");
          link.href = `data:application/xml;base64,${response.xml}`;
          link.download = response.filename || `NFe_${chave}.xml`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.$toast.success("XML baixado com sucesso!");
        } else {
          this.$toast.error("Erro ao obter XML");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Erro ao baixar XML: " + (err.message || "Erro desconhecido"));
      }
    },

    openDialogCancel(id) {
      this.$refs.cancelForm.open(id);
    },

    applyFilter() {
      if (this.filterApply) {
        this.filterApply = false;
        this.itemsFiltered = false;
        this.getInvoices();
      } else {
        this.filterApply = true;
        this.$refs.filterForm.open();
      }
    },

    setInvoice(event) {
      this.form = {
        customer_id: event.customer.id,
        platform: "AVULSA",
        platform_transaction: "AVULSA" + event.customer.id + event.emited_at,
        value: event.value,
        billed_at: event.emited_at,
        billed: false,
        ordered_at: event.emited_at,
        service_id: event.service,
        payment_type: "AVULSA",
        receiver_type: "CREDITO",
        status: "order",
        description: event.description,
      };

      if (event.service == 1) {
        this.form.receiver_type = "SELLER";
      }
      if (event.service == 2) {
        this.form.receiver_type = "AFFILIATE";
      }
      if (event.service == 3) {
        this.form.receiver_type = "DROPSHIPPER";
      }

      this.$http
        .$post("/order", this.form)
        .then((response) => {
          const orderId = [];

          orderId.push(response.response.id);

          this.makeInvoice(response.response.id);
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async makeInvoice(id) {
      await this.$http
        .$post("/nota-fiscal-faturar", {
          orders: id,
          billed_at: this.form.billed_at,
        })
        .then((response) => {
          this.getInvoices();
          console.log(response);
        })
        .catch((err) => {
          console.log(err);
        });
    },

    selectAll() {
      this.selectedInvoices = this.selected
        ? []
        : this.invoices.filter(
            (element) =>
              element.status != "billed" && element.status != "canceled"
          );
      this.selected = this.selected ? false : true;
    },

    filter(filter) {
      this.itemsFiltered = filter;
      this.getInvoices();
    },
  },
};
</script>

<style>
.bg-gray-50 {
  background-color: #f9fafb !important;
}

.btn-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%) !important;
  color: white !important;
  transition: all 0.3s ease;
}

.btn-gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

.custom-table ::v-deep thead th {
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161 !important;
  font-size: 0.75rem !important;
  background-color: #fafafa !important;
  height: 48px !important;
}

.custom-table ::v-deep tbody tr:hover {
  background-color: #f5f7fa !important;
}

.search-field ::v-deep .v-input__slot {
  min-height: 44px !important;
}

.mono-font {
  font-family: 'SF Mono', 'Roboto Mono', 'Menlo', monospace !important;
  font-size: 0.85rem;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
